import {
  DENTIST_HIDDEN_MENU_PAGES,
  isDentistUser,
  isSubClientAccount,
  SUB_ACCOUNT_HIDDEN_MENU_PAGES,
  VENDOR_HIDDEN_MENU_PAGES,
} from './dentistPortal';

const ALWAYS_HIDDEN_MENU_NAMES = [
  'formal_types',
  'units',
  'unit_types',
  'branchs',
  'assets',
  'colors',
  'delivery_centers',
  'dentists',
  'wallets',
  'activities',
  'transactions',
  'expenses',
  'expense_types',
  'delivery_types',
  'delivery_type',
  'deliverytypes',
  'delivery-types',
];

const SUBSCRIPTION_CHILD = {
  name: 'setting.subscription',
  title: 'Subscription',
  path: '/setting/subscription',
  icon: 'credit_card',
  is_menu_item: true,
  translation_key: 'sidebar.setting_subscription',
};

export const buildHiddenMenuNames = (user) => {
  const hidden = new Set(ALWAYS_HIDDEN_MENU_NAMES);

  if (isDentistUser(user)) {
    DENTIST_HIDDEN_MENU_PAGES.forEach((name) => hidden.add(name));
    if (isSubClientAccount(user)) {
      SUB_ACCOUNT_HIDDEN_MENU_PAGES.forEach((name) => hidden.add(name));
    }
  } else {
    VENDOR_HIDDEN_MENU_PAGES.forEach((name) => hidden.add(name));
  }

  return hidden;
};

const isAdminUser = (user) =>
  String(user?.user_type_name ?? user?.user_type?.name ?? user?.userType?.name ?? '')
    .toLowerCase() === 'admin';

/**
 * Same visible sidebar entries the SideBar renders (including admin subscription).
 */
export const getVisibleSidebarMenu = (menu, user) => {
  if (!Array.isArray(menu)) {
    return [];
  }

  const hidden = buildHiddenMenuNames(user);
  const filtered = menu.filter((entry) => !hidden.has(entry?.name));

  if (!isAdminUser(user) || isDentistUser(user)) {
    return filtered;
  }

  let hasPlatformSettings = false;
  const withSubscription = filtered.map((entry) => {
    if (entry?.name !== 'platform_settings') {
      return entry;
    }

    hasPlatformSettings = true;
    const children = Array.isArray(entry.children) ? [...entry.children] : [];
    const alreadyPresent = children.some(
      (child) =>
        child?.name === 'setting.subscription' ||
        child?.name === 'platform_settings.subscription' ||
        child?.path === '/setting/subscription'
    );

    if (alreadyPresent) {
      return entry;
    }

    return { ...entry, children: [...children, SUBSCRIPTION_CHILD] };
  });

  if (hasPlatformSettings) {
    return withSubscription;
  }

  const platformSettingsEntry = {
    name: 'platform_settings',
    title: 'Platform Settings',
    path: '/platform-settings',
    icon: 'category',
    is_menu_item: true,
    translation_key: 'sidebar.platform_settings',
    children: [SUBSCRIPTION_CHILD],
  };

  const labSettingsIndex = withSubscription.findIndex((entry) => entry?.name === 'setting');
  if (labSettingsIndex >= 0) {
    const next = [...withSubscription];
    next.splice(labSettingsIndex + 1, 0, platformSettingsEntry);
    return next;
  }

  return [...withSubscription, platformSettingsEntry];
};

const isTourableMenuItem = (entry) =>
  Boolean(entry?.path) && entry?.is_menu_item !== false && typeof entry.path === 'string';

/**
 * Flatten visible sidebar entries into navigable leaf pages for the system tour.
 */
export const flattenSidebarTourPages = (menu, user) => {
  const pages = [];

  getVisibleSidebarMenu(menu, user).forEach((entry) => {
    const children = Array.isArray(entry?.children)
      ? entry.children.filter((child) => child?.is_menu_item !== false)
      : [];

    if (children.length > 0) {
      children.forEach((child) => {
        if (isTourableMenuItem(child)) {
          pages.push({
            name: child.name,
            path: child.path,
            title: child.title ?? child.name,
            parentName: entry.name,
            translation_key: child.translation_key ?? child?.meta?.translationKey,
            meta: child.meta,
          });
        }
      });
      return;
    }

    if (isTourableMenuItem(entry)) {
      pages.push({
        name: entry.name,
        path: entry.path,
        title: entry.title ?? entry.name,
        parentName: null,
        translation_key: entry.translation_key ?? entry?.meta?.translationKey,
        meta: entry.meta,
      });
    }
  });

  return pages;
};

export const tourMenuTargetId = (name) =>
  `tour-menu-${String(name ?? '').replace(/[^a-zA-Z0-9_-]/g, '-')}`;
